import {format, isValid, parseISO} from 'date-fns'
import memoizeOne from 'memoize-one'
import {
  CustomUserProfileField,
  FilterCondition,
  FilterOperator,
  UserProfileKnownPropsCodec
} from '../../common/types'
import {
  EDITABLE_PROFILE_FIELDS,
  RESERVED_FIELD_PREFIX
} from '../../common/types/constants'
import {allProfileFields} from '../../components/filter-modal/fields'
import {ListColumn} from '../../components/library/list'
import {
  ProfileFiltersInput,
  SyncedEndUsersOrderBy
} from '../../data/workbench-api/generated/types'
import {getString, upperCaseFirst} from '../../lib/helpers'

type FieldTypes = 'date' | 'text'
export type BaseCustomerRow = {id: string; profile: unknown}

export type Field<CustomerRow extends BaseCustomerRow> = {
  name: string
  isEditable: boolean
  isCustom: boolean
  isAnalytics: boolean
  label: string
  type: FieldTypes
  displayValue: (user: CustomerRow) => string | null
  sortKey?: string
}
export const CUSTOM_FIELD_SORT_PREFIX = '__CSK__'
export const customFieldSortRegex = new RegExp(`^${CUSTOM_FIELD_SORT_PREFIX}`)
const fieldNameCreated = `${RESERVED_FIELD_PREFIX}created`
export const fieldNameId = `${RESERVED_FIELD_PREFIX}id`
export const getFieldDefinitions = memoizeOne(
  <CustomerRow extends BaseCustomerRow>(
    customFields: CustomUserProfileField[],
    customEventsFields?: {name: string}[]
  ): Field<CustomerRow>[] => {
    const systemFields: {
      name: string
      sortKey?: string
      label?: string
      type?: FieldTypes
    }[] = [
      {
        name: fieldNameId,
        sortKey: SyncedEndUsersOrderBy.IdAsc
      },
      {
        name: fieldNameCreated,
        type: 'date',
        sortKey: SyncedEndUsersOrderBy.CreatedAsc
      },
      ...Object.keys(UserProfileKnownPropsCodec.props)
        // avoid duplicating the special fields added above
        .filter(field => ![fieldNameId, fieldNameCreated].includes(field))
        .map(field => ({
          name: field
        }))
    ]

    const allFields = systemFields.concat([
      ...customFields,
      ...(customEventsFields ?? []).map(
        field =>
          ({
            name: field.name,
            type: 'date'
          } as CustomUserProfileField)
      )
    ])
    // .sort((a, b) => a.name.localeCompare(b.name)) not sorting yet, but an option
    return allFields.map(field => {
      const isCustom = !systemFields.some(sf => sf.name === field.name)
      const isCustomEvent = !!customEventsFields?.some(
        cef => cef.name === field.name
      )
      const isEditable =
        !isCustom &&
        (EDITABLE_PROFILE_FIELDS.includes(field.name) || isCustomEvent)
      const isAnalytics = (!isCustom && !isEditable) || isCustomEvent
      const type = field.type ?? (field.name.match(/_at$/) ? 'date' : 'text') // naive field type assumption based on names for now
      const label = field.label ?? humanFriendlyProfileField(field.name)
      const sortKey =
        field.sortKey ??
        (isCustom // todo not sure how to sort on custom fields currently, graphile doesn't know they exist
          ? `${CUSTOM_FIELD_SORT_PREFIX}${field.name.toUpperCase()}_ASC`
          : `${field.name.toUpperCase()}_ASC`)
      // ^ see synced-user-profile-sort for where we define these
      const displayProfileValue = (user: CustomerRow) => {
        const value = getString(user.profile, field.name)
        if (!value) {
          return null
        }
        return type === 'date' ? displayDate(value) : value
      }

      return {
        name: field.name,
        isEditable,
        isAnalytics,
        isCustom,
        label,
        type,
        displayValue: displayProfileValue,
        sortKey
      }
    })
  }
)

export const displayDate = (timestamp: string, withTime = true) => {
  const date = parseISO(timestamp)
  return isValid(date)
    ? format(
        parseISO(timestamp),
        `${withTime ? 'h:mm a, ' : ''}EEE d MMM, yyyy`
      )
    : null
}

// first_cancelled_card_at -> card-cancelled first seen at
// last_published_card_at -> card-published last seen at
export const humanFriendlyProfileField = (fieldName: string) => {
  // use the statically defined value from the filter modal, if there is one
  // if not, fall back to a generated label
  const fieldLabel = allProfileFields.find(f => f.name === fieldName)?.label
  if (fieldLabel) {
    return fieldLabel
  }

  if (fieldName === 'last_seen_at' || fieldName === 'first_seen_at') {
    return upperCaseFirst(fieldName).replace(/_/g, ' ')
  }

  const firstLastPrefix = ['first', 'last']
  for (const prefix of firstLastPrefix) {
    if (fieldName.startsWith(`${prefix}_`) && fieldName.endsWith('_at')) {
      let strippedField = `${fieldName
        .replace(`${prefix}_`, '')
        .replace('_at', '')
        .replace(/_/g, '-')}`
      if (strippedField.endsWith('-card')) {
        strippedField = `card-${strippedField.replace('-card', '')}`
      }

      return upperCaseFirst(
        `${strippedField.replace(/-/g, ' ')} ${prefix} seen at`
      )
    }
  }

  return upperCaseFirst(fieldName).replace(/_/g, ' ')
}

export const makeEndUserColumn = <CustomerRow extends BaseCustomerRow>(
  field: Field<CustomerRow>
): ListColumn<CustomerRow, string> => ({
  id: field.name,
  header: field.label,
  accessor: r => {
    return field.displayValue(r)
  },
  sortKey: field.sortKey
})

export const transformConditionsForAPI = (
  conditions: FilterCondition[],
  operator: FilterOperator
): ProfileFiltersInput => {
  // we treat empty conditions in sub fields as falsy (perhaps we should re-evalute that decision)
  if (!conditions.length) return {operator: 'AND', conditions: []}
  return {
    // Top level operator, only applies when several top-level
    // conditions which this component does not do do.
    operator: 'AND',
    conditions: [
      {
        operator,
        // @ts-ignore as graphql expects value to always be present
        // due to not being able to handle union types
        // IS_EMPTY && NOT_EMPTY conditions won't set value, but we can't express this in GQL
        conditions: conditions as ProfileFiltersInput['conditions']
      }
    ]
  }
}

type GroupedFilters<
  R extends BaseCustomerRow,
  T extends {id: string; name: string; enabled?: boolean | null}
> = {
  tags: (FilterCondition & {
    matchedTag: T
    index: number
  })[]
  profile: (FilterCondition & {
    matchedField: Field<R>
    index: number
  })[]
  invalidTag: (FilterCondition & {index: number})[]
  invalidProfile: (FilterCondition & {index: number})[]
}

export const groupFilters = <
  R extends BaseCustomerRow,
  T extends {id: string; name: string; enabled?: boolean | null}
>(
  filterConditions: FilterCondition[],
  fieldDefinitions: Field<R>[],
  tags: T[]
): GroupedFilters<R, T> =>
  filterConditions.reduce<GroupedFilters<R, T>>(
    (acc, curr, index) => {
      /* eslint-disable functional/immutable-data */
      if (curr.field === `${RESERVED_FIELD_PREFIX}tag`) {
        const tag =
          'value' in curr ? tags.find(tg => tg.id === curr.value) : undefined
        if (tag) {
          acc.tags = [...acc.tags, {...curr, matchedTag: tag, index}]
          return acc
        }
        acc.invalidTag = [...acc.invalidTag, {...curr, index}]
        return acc
      }
      const field = fieldDefinitions.find(c => c.name === curr.field)
      if (field) {
        acc.profile = [...acc.profile, {...curr, matchedField: field, index}]
        return acc
      }
      acc.invalidProfile = [...acc.invalidProfile, {...curr, index}]
      return acc
    },
    {tags: [], profile: [], invalidTag: [], invalidProfile: []}
  )

export const filterConditionsEqual = (
  filterConditions: FilterCondition[],
  originalFilterConditions: FilterCondition[]
) => {
  if (filterConditions.length !== originalFilterConditions.length) return false
  const hash = (cond: FilterCondition) =>
    `${cond.field}${cond.condition}${'value' in cond ? cond.value : ''}`

  const aHashed = filterConditions.map(hash).sort((a, b) => a.localeCompare(b))
  const bHashed = originalFilterConditions
    .map(hash)
    .sort((a, b) => a.localeCompare(b))

  return aHashed.every((aHash, index) => aHash === bHashed[index])
}
